import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Space, InputNumber, Button, Typography, Checkbox, Select, message, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, MinusCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { CreatePuja } from '../types';
import { pujaTagAPI } from '../services/api';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

interface AddPujaModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: CreatePuja & { tagIds?: number[] } | { formData: FormData; tagIds?: number[] }) => Promise<void>;
}

const AddPujaModal: React.FC<AddPujaModalProps> = ({
  visible,
  onCancel,
  onSubmit
}) => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState<any[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeTagFieldName, setActiveTagFieldName] = useState<number | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleCreateTag = async (fieldName?: number) => {
    const fieldToSet = fieldName !== undefined ? fieldName : activeTagFieldName;
    if (searchValue.trim()) {
      setCreatingTag(true);
      const tagId = await createTag(searchValue.trim());
      if (tagId !== null && fieldToSet !== null) {
        // Set the newly created tag in the active field
        form.setFieldValue(['tagIds', fieldToSet], tagId);
      }
      setSearchValue('');
      setCreatingTag(false);
      setActiveTagFieldName(null);
    }
  };

  useEffect(() => {
    if (visible) {
      loadTags();
    }
  }, [visible]);

  const loadTags = async () => {
    setLoadingTags(true);
    try {
      const response = await pujaTagAPI.getAll();
      setTags(response.data);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const createTag = async (name: string) => {
    try {
      const response = await pujaTagAPI.createTag({ name });
      setTags(prev => [...prev, response.data]);
      return response.data.id;
    } catch (error) {
      console.error('Error creating tag:', error);
      message.error('Failed to create tag');
      return null;
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSearchValue('');
    setFileList([]);
    onCancel();
  };

  const handleSubmit = async (values: any) => {
    // Process metadata similar to GranthaPage
    const metadata: any = {
      bidha: values.bidha || '',
      upabidha: values.upabidha || '',
      prakar: values.prakar || '',
      upaprakar: values.upaprakar || '',
      aupaprakar: values.aupaprakar || '',
      ...(values.description ? { description: values.description } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
    };
    
    if (values.customMetadata) {
      values.customMetadata.forEach((item: { key: string; value: string }) => {
        if (item.key && item.value) {
          metadata[item.key] = item.value;
        }
      });
    }
    
    // Check if profile picture is present
    const hasProfilePicture = fileList.length > 0 && fileList[0].originFileObj;
    
    if (hasProfilePicture) {
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('metadata', JSON.stringify(metadata));
      if (values.lines) {
        formData.append('lines', JSON.stringify(values.lines));
      }
      formData.append('profilePicture', fileList[0].originFileObj);
      
      const pujaData = {
        formData,
        tagIds: values.tagIds?.filter((id: any) => id !== undefined) || []
      };
      
      await onSubmit(pujaData);
    } else {
      // Use regular JSON
      const pujaData: CreatePuja & { tagIds?: number[] } = {
        name: values.name,
        lines: values.lines,
        metadata: metadata,
        tagIds: values.tagIds?.filter((id: any) => id !== undefined) || []
      };
      
      await onSubmit(pujaData);
    }
    
    form.resetFields();
    setSearchValue('');
    setFileList([]);
  };

  return (
    <Modal
      title="Add Puja"
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      width={800}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter puja name' }]}>
          <Input placeholder="Enter puja name" />
        </Form.Item>
        
        <Form.Item label="Profile Picture" name="profilePicture">
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            accept="image/*"
            fileList={fileList}
            onChange={({ fileList }) => setFileList(fileList)}
            listType="picture-card"
          >
            {fileList.length < 1 && <div><UploadOutlined /><div style={{ marginTop: 8 }}>Upload</div></div>}
          </Upload>
        </Form.Item>
        
        <Title level={5}>Metadata</Title>
        
        <div style={{ border: '1px dashed #d9d9d9', padding: 16, borderRadius: 6, marginBottom: 16 }}>
          <Form.Item 
            name="bidha" 
            label="Bidha"
            rules={[{ required: true, message: 'Bidha is required in metadata' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item name="upabidha" label="Upabidha">
            <Input />
          </Form.Item>
          
          <Form.Item name="prakar" label="Prakar">
            <Input />
          </Form.Item>
          
          <Form.Item name="upaprakar" label="Upaprakar">
            <Input />
          </Form.Item>
          
          <Form.Item name="aupaprakar" label="Aupaprakar">
            <Input />
          </Form.Item>
          
          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter description" />
          </Form.Item>
          
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>
          
          <Form.List name="customMetadata">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'key']}
                      rules={[{ required: true, message: 'Key required' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Key" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'value']}
                      rules={[{ required: true, message: 'Value required' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Value" />
                    </Form.Item>
                    <Button 
                      type="text" 
                      danger 
                      icon={<MinusCircleOutlined />} 
                      onClick={() => remove(name)}
                      style={{ marginTop: 4 }}
                    />
                  </div>
                ))}
                <div style={{ textAlign: 'center', marginTop: fields.length > 0 ? 16 : 8 }}>
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                    Additional Metadata
                  </Button>
                </div>
              </>
            )}
          </Form.List>
        </div>
        
        <Title level={5} style={{ marginTop: 16 }}>Lines</Title>
        
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <div key={key} style={{ border: '1px solid #f0f0f0', padding: 12, borderRadius: 4, marginBottom: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      width: '100%',
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, 'content']}
                      rules={[{ required: true, message: 'Missing content' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <TextArea placeholder="Line content" rows={3} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'isVisible']}
                      valuePropName="checked"
                      initialValue={true}
                      style={{ marginBottom: 0, flexShrink: 0 }}
                    >
                      <Checkbox>Visible</Checkbox>
                    </Form.Item>
                    <Form.Item 
                      {...restField} 
                      name={[name, 'order']} 
                      initialValue={index + 1}
                      style={{ marginBottom: 0, flexShrink: 0, width: 80 }}
                    >
                      <InputNumber placeholder="Order" min={1} readOnly value={index + 1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Button
                      onClick={() => remove(name)}
                      icon={<DeleteOutlined />}
                      style={{ flexShrink: 0 }}
                    />
                  </div>
                </div>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Line
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item label="Tags">
          <Form.List name="tagIds">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Select
                        showSearch
                        placeholder="Search or create tag"
                        loading={loadingTags}
                        onSearch={handleSearch}
                        filterOption={(input, option) =>
                          (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                        onDropdownVisibleChange={(open) => {
                          if (open) {
                            setActiveTagFieldName(name);
                          }
                        }}
                        notFoundContent={
                          searchValue ? (
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={() => handleCreateTag(name)}
                              loading={creatingTag}
                              block
                            >
                              Create "{searchValue}"
                            </Button>
                          ) : null
                        }
                      >
                        {tags.map(tag => (
                          <Option key={tag.id} value={tag.id}>
                            {tag.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Button
                      onClick={() => remove(name)}
                      style={{ marginLeft: 8 }}
                      danger
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  Add Tag
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddPujaModal;